import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DamNumberInput from '../shared/inputs/DamNumberInput.js';
import { Fetch } from '../utils/Utils.js';
import './EditableDamSireField.css';

// Inline Dam/Sire editor for the Herd Animal detail view (desktop
// HerdExpandedComponent + mobile HerdCard) — commits on blur, only if
// changed, same pattern as ListHerdAnimals.js's TagCell. Both PATCH
// /api/animals/{tag}/birth, which already accepts dam/father — no backend
// change needed for this (Trello 9viD3Fto).
//
// DamNumberInput has no built-in way to flip itself back to its read-only
// "TAG-1234" display after a successful save — its `edit` state is only
// read from props once, on mount, and never re-synced afterward. Keying
// this wrapper by the current value forces a clean remount once a save
// actually changes it, and does nothing on a failed save, so the user
// doesn't lose an in-progress edit.
//
// The API rejects a dam/sire of the known opposite sex with a
// dam_must_be_female / sire_must_be_male code, which Fetch() already
// translates — that reason is shown as-is; anything else falls back to the
// generic "check the tag number" message.
export function EditableDamSireField({ tag, field, value, onSaved }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const commit = (e) => {
    const trimmed = e.target.value.trim();
    if (trimmed === (value || '')) return;
    setSaving(true);
    setError('');
    Fetch(`/api/animals/${tag}/birth`, 'PATCH', JSON.stringify({ [field]: trimmed }),
      () => { setSaving(false); onSaved(); },
      (message) => {
        setSaving(false);
        const sexError = t(field === 'dam' ? 'dam_must_be_female' : 'sire_must_be_male');
        setError(message === sexError ? sexError : t(field === 'dam' ? 'dam_update_failed' : 'sire_update_failed'));
      }
    );
  };

  return (
    <span className="dam-sire-field">
      <DamNumberInput
        key={value || ''}
        value={value}
        edit={false}
        placeholderKey={field === 'dam' ? 'dam_tag_number' : 'sire_tag_number'}
        onBlur={commit}
      />
      {saving && <span className="dam-sire-field-saving">…</span>}
      {error && <span className="dam-sire-field-error">{error}</span>}
    </span>
  );
}

export default EditableDamSireField;
